import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { getFirestore, doc, getDoc } from '@firebase/firestore';
import { getAuth, onAuthStateChanged } from '@firebase/auth';

import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useFacility } from '../../context/FacilityContext';
import { changePassword } from '../../firebase/auth';

import './AccountSettings.css';

function AccountSettings() {
  const { facilityId } = useFacility();
  const { userName, userEmail } = useAuth('');

  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [infoAlert, setInfoAlert] = useState('');
  const [errorAlert, setErrorAlert] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const resetAlerts = () => {
    setInfoAlert('');
    setErrorAlert('');
  };

  const [pinCode, setPinCode] = useState(null);
  const [pinLoading, setPinLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setPinCode(null);
        setPinLoading(false);
        return;
      }

      try {
        const db = getFirestore();
        const userRef = doc(db, 'users', user.uid);
        const snap = await getDoc(userRef);

        setPinCode(snap.exists() ? snap.data().pinCode ?? null : null);
      } catch (err) {
        console.error(err);
        setErrorAlert('Could not load your PIN. Please try again later.');
        setPinCode(null);
      } finally {
        setPinLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const changePass = async (event) => {
    event.preventDefault();
    resetAlerts();
    if (newPassword !== passwordConfirm) {
      setErrorAlert('Passwords do not match. Please try again.');
      return;
    }
    if (newPassword.length < 8) {
      setErrorAlert('Password has to be longer than 8 characters!');
      return;
    }
    setIsLoading(true);
    try {
      const result = await changePassword(password, newPassword);
      if (!result) {
        setInfoAlert('Password updated successfully!');
        setPassword('');
        setPasswordConfirm('');
        setNewPassword('');
      } else {
        setErrorAlert(result);
      }
    } catch (err) {
      console.error('Error updating password:', err);
      setErrorAlert(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
      <Typography variant="h4" component="h1" gutterBottom>
        Welcome, {userName}
      </Typography>
      <Typography variant="body1">{userEmail}</Typography>

      <Box className="SettingsContainer" sx={{ mt: 4 }}>
        <Typography variant="h6">
          Your Staff PIN&nbsp;(for&nbsp;QuickCare)
        </Typography>
        {pinLoading ? (
          <CircularProgress size={24} sx={{ mt: 1 }} />
        ) : pinCode ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Your&nbsp;PIN:&nbsp;{pinCode}
            </Typography>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mt: 1 }}>
            No PIN assigned. Ask an administrator to generate one for you.
          </Alert>
        )}
      </Box>

      <Box className="SettingsContainer" sx={{ mt: 6 }}>
        <Typography variant="h6">Change Password</Typography>

        {infoAlert && (
          <Alert
            severity="success"
            onClose={() => setInfoAlert('')}
            sx={{ mb: 2 }}
          >
            {infoAlert}
          </Alert>
        )}
        {errorAlert && (
          <Alert
            severity="error"
            onClose={() => setErrorAlert('')}
            sx={{ mb: 2 }}
          >
            {errorAlert}
          </Alert>
        )}

        <form onSubmit={changePass}>
          <TextField
            label="Current Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            autoComplete="current-password"
            required
            fullWidth
          />
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            margin="normal"
            autoComplete="new-password"
            required
            fullWidth
            inputProps={{ minLength: 8 }}
            error={newPassword.length > 0 && newPassword.length < 8}
            helperText={
              newPassword.length > 0 &&
              newPassword.length < 8 &&
              'Password has to be longer than 8 characters!'
            }
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            margin="normal"
            autoComplete="new-password"
            required
            fullWidth
            inputProps={{ minLength: 8 }}
            error={newPassword !== passwordConfirm}
            helperText={
              newPassword !== passwordConfirm && 'Passwords do not match!'
            }
          />

          <Button
            className="FormButton"
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading}
            sx={{ mt: 2 }}
          >
            Change
          </Button>
        </form>
      </Box>
    </DashboardLayout>
  );
}

export default AccountSettings;
